import { useEffect, useState, useMemo } from "react";
import {
  Modal,
  Button,
  Form,
  Alert,
  OverlayTrigger,
  Tooltip,
} from "react-bootstrap";

import { FaInfoCircle } from "react-icons/fa";

import http from "services/httpClient";
import { getTimezoneOptions } from "utils/timezones";
import { DateTime } from "luxon";

interface Props {
  open: boolean;
  deviceId: number;
  onClose: () => void;
}

interface HeartbeatSettings {
  device_id: number;
  threshold_minutes: number;
  timezone: string;
  enabled: boolean;
}

export default function HeartbeatSettingsModal({
  open,
  deviceId,
  onClose,
}: Props) {
  const [loading, setLoading] = useState(false);

  const [thresholdMinutes, setThresholdMinutes] =
    useState("1440");

  const [timezone, setTimezone] =
    useState("Australia/Sydney");

  const [enabled, setEnabled] =
    useState(true);

  const [error, setError] =
    useState<string | null>(null);

  const [success, setSuccess] =
    useState<string | null>(null);

  const timezones = useMemo(
    () => getTimezoneOptions(DateTime.now()),
    []
  );

  useEffect(() => {
    if (!open) return;

    loadSettings();
  }, [open, deviceId]);

  const loadSettings = async () => {
    try {
      setLoading(true);

      const res = await http.get<HeartbeatSettings>(
        `/device-heartbeat/${deviceId}`
      );

      setThresholdMinutes(
        String(res.data.threshold_minutes)
      );

      setTimezone(
        res.data.timezone ??
        "Australia/Sydney"
      );

      setEnabled(
        res.data.enabled ?? true
      );

      setError(null);
    } catch (err) {
      console.error(err);
      setError(
        "Failed to load heartbeat settings."
      );
    } finally {
      setLoading(false);
    }
  };

  const saveSettings = async () => {
    const minutes = Number(
      thresholdMinutes
    );

    if (
      isNaN(minutes) ||
      minutes < 60 ||
      minutes > 1440
    ) {
      setError(
        "Threshold must be between 5 and 1440 minutes."
      );
      return;
    }

    try {
      setLoading(true);
      setError(null);

      await http.put(
        `/device-heartbeat/${deviceId}`,
        {
          threshold_minutes: minutes,
          timezone,
          enabled,
        }
      );

      setSuccess(
        "Heartbeat settings updated."
      );

      setTimeout(() => {
        onClose();
      }, 500);

    } catch (err) {
      console.error(err);

      setError(
        "Failed to save heartbeat settings."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      show={open}
      onHide={onClose}
      centered
    >
      <Modal.Header closeButton>
        <Modal.Title>
          Device Heartbeat Settings
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>

        {error && (
          <Alert variant="danger">
            {error}
          </Alert>
        )}

        {success && (
          <Alert variant="success">
            {success}
          </Alert>
        )}

        <Form.Group className="mb-3">
          <Form.Label>
            Threshold Minutes

            <OverlayTrigger
              placement="right"
              overlay={
                <Tooltip id="heartbeat-tooltip">
                  <div>
                    1 hour = 60 mins
                  </div>

                  <div>
                    2 hours = 120 mins
                  </div>

                  <div>
                    12 hours = 720 mins
                  </div>

                  <div>
                    24 hours = 1440 mins
                  </div>
                </Tooltip>
              }
            >
              <span
                style={{
                  marginLeft: 8,
                  cursor: "pointer",
                }}
              >
                <FaInfoCircle
                  style={{
                    color: "#0d6efd",
                  }}
                />
              </span>
            </OverlayTrigger>
          </Form.Label>

          <Form.Control
            type="text"
            value={thresholdMinutes}
            onChange={(e) => {
              const value =
                e.target.value;

              if (
                /^\d*$/.test(value)
              ) {
                setThresholdMinutes(
                  value
                );
              }
            }}
          />

          <Form.Text muted>
            Minimum: 60 mins |
            Maximum: 1440 mins
          </Form.Text>
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>
            Timezone
          </Form.Label>

          <Form.Select
            value={timezone}
            onChange={(e) =>
              setTimezone(
                e.target.value
              )
            }
          >
            {timezones.map((tz) => (
              <option
                key={tz.value}
                value={tz.value}
              >
                {tz.label}
              </option>
            ))}
          </Form.Select>
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Check
            type="checkbox"
            label="Heartbeat Enabled"
            checked={enabled}
            onChange={(e) =>
              setEnabled(
                e.target.checked
              )
            }
          />
        </Form.Group>

      </Modal.Body>

      <Modal.Footer>
        <Button
          variant="secondary"
          onClick={onClose}
          disabled={loading}
        >
          Cancel
        </Button>

        <Button
          variant="primary"
          onClick={saveSettings}
          disabled={loading}
        >
          Save
        </Button>
      </Modal.Footer>
    </Modal>
  );
}